import React, { useMemo, useCallback } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { Stethoscope } from "lucide-react";
import { 
  useGetSpecialitiesQuery,
  useSpecialityFilters,
  useSpecialityActions,
  SpecialityDetailsModal,
  createSpecialityColumns,
  createSpecialityActions,
} from "@entities/specialities";
import { SpecialityCreateModal, SpecialityEditModal } from "@entities/specialities/components/SpecialityCreateEditModal";
import EntityTableSection from "@/components/section/EntityTableSection";
import PageHeader from "@/components/ui/PageHeader";
import SearchAndFiltersSection from "@/components/section/SearchAndFiltersSection";
import ErrorAlert from "@/components/ErrorAlert";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import ConfirmationModal from "@/components/modals/ConfirmationModal";

const SpecialitiesPage: React.FC = () => {
  // Performance optimized with memoized hooks and callbacks
  const { filters, queryParams, handleFilterChange } = useSpecialityFilters();
  const actions = useSpecialityActions();

  const {
    data: specialities,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetSpecialitiesQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const specialitiesArray = specialities || [];

  // Memoize filtered specialities to prevent recalculation on every render
  const filteredSpecialities = useMemo(() => {
    return specialitiesArray.filter((speciality) => {
      const searchTerm = filters.search?.toLowerCase() || "";
      const matchesSearch =
        !searchTerm || speciality.title.toLowerCase().includes(searchTerm);
      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "shown" && speciality.is_shown) ||
        (filters.status === "hidden" && !speciality.is_shown);

      return matchesSearch && matchesStatus;
    });
  }, [specialitiesArray, filters.search, filters.status]);

  // Memoize search filters configuration
  const searchFilters = useMemo(
    () => [
      {
        key: "status",
        label: "All Visibility",
        value: filters.status,
        options: [
          { value: "all", label: "All Visibility" },
          { value: "shown", label: "Shown" },
          { value: "hidden", label: "Hidden" },
        ],
        onChange: (value: string) => handleFilterChange("status", value),
      },
    ],
    [filters.status, handleFilterChange]
  );

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => createSpecialityColumns(), []);

  // Memoize table actions
  const tableActions = useMemo(
    () => createSpecialityActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler to prevent inline function re-renders
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize modal success handler to refresh data after create/update
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading && !specialitiesArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load specialities"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto">
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Specialities Management"
          subtitle={`${specialitiesArray.length} total specialities`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Create Speciality"
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search by title..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection
          data={filteredSpecialities}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: Stethoscope,
            title: "No specialities found",
            description: "Create your first speciality to get started",
          }}
        />

        {/* Hash-managed Modals - Each modal self-determines if it should render */}
        <SpecialityDetailsModal />

        <SpecialityCreateModal
          onSuccess={handleModalSuccess}
        />

        <SpecialityEditModal
          onSuccess={handleModalSuccess}
        />

        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </VStack>
    </Box>
  );
};

export default SpecialitiesPage;
